'use client';

import Image from 'next/image';
import { Sneaker } from '@/types/sneaker';
import { useTranslation } from '@/lib/i18n/LanguageContext';

/** StockX serves 140x100 thumbnails by default; ask for a size that holds up on the big stage. */
function toHighRes(url: string) {
  try {
    const u = new URL(url);
    if (u.hostname === 'images.stockx.com') {
      u.searchParams.set('w', '800');
      u.searchParams.set('h', '600');
      u.searchParams.set('dpr', '1');
    }
    return u.toString();
  } catch {
    return url;
  }
}

interface SneakerStageProps {
  sneaker: Sneaker;
  /** Below lg, shrink to a thumbnail + name row so the result panel has room (desktop is unaffected). */
  compact?: boolean;
}

/** Fills whatever height its parent gives it: the image area flexes, the title block doesn't. */
export function SneakerStage({ sneaker, compact = false }: SneakerStageProps) {
  const { t } = useTranslation();

  return (
    <section
      className={`card anim-rise flex min-w-0 flex-col overflow-hidden ${
        compact ? 'max-lg:flex-none max-lg:flex-row max-lg:items-center' : ''
      } min-h-[9rem] flex-1 ${compact ? 'max-lg:min-h-0' : ''}`}
    >
      <div
        className={`stage relative min-h-0 flex-1 ${
          compact ? 'max-lg:h-20 max-lg:w-28 max-lg:flex-none' : ''
        }`}
      >
        {sneaker.imageUrl ? (
          <Image
            src={toHighRes(sneaker.imageUrl)}
            alt={sneaker.name}
            fill
            sizes="(max-width: 1024px) 100vw, 640px"
            className={`object-contain mix-blend-multiply ${
              compact ? 'p-4 max-lg:p-1.5 sm:p-8 max-lg:sm:p-1.5' : 'p-4 sm:p-8'
            }`}
            priority
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-neutral-500">
            {t('noImageAvailable')}
          </div>
        )}
        {sneaker.brand && (
          <span
            className={`absolute left-3 top-3 rounded-full bg-ink px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-fg ${
              compact ? 'max-lg:hidden' : ''
            }`}
          >
            {sneaker.brand}
          </span>
        )}
      </div>

      <div className={`shrink-0 px-4 py-3 ${compact ? 'max-lg:min-w-0 max-lg:flex-1' : ''}`}>
        <h1
          className={`font-display font-extrabold leading-tight tracking-tight ${
            compact ? 'max-lg:text-base' : 'text-lg'
          } sm:text-xl lg:text-2xl`}
        >
          {sneaker.name}
        </h1>
        {/* Colorway/reference are dropped on short screens and in compact mode to keep the photo big */}
        {(sneaker.colorway || sneaker.styleCode) && (
          <dl
            className={`mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs [@media(max-height:760px)]:hidden ${
              compact ? 'max-lg:hidden' : ''
            }`}
          >
            {sneaker.colorway && (
              <div className="flex gap-1.5">
                <dt className="uppercase tracking-widest text-muted">{t('colorway')}</dt>
                <dd className="font-medium">{sneaker.colorway}</dd>
              </div>
            )}
            {sneaker.styleCode && (
              <div className="flex gap-1.5">
                <dt className="uppercase tracking-widest text-muted">{t('reference')}</dt>
                <dd className="font-mono font-medium">{sneaker.styleCode}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </section>
  );
}
